/** すべての日付はローカルタイムの YYYY-MM-DD 文字列で扱う (UTC ずれを避ける) */

export const toKey = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const today = (): string => toKey(new Date());

export const parseKey = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};

export const shiftDays = (key: string, delta: number): string => {
  const d = parseKey(key);
  d.setDate(d.getDate() + delta);
  return toKey(d);
};

export const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'] as const;

export const weekdayOf = (key: string): string => WEEKDAYS[parseKey(key).getDay()] ?? '';

/** その日を含む週の月曜日 (週の集計の起点) */
export const mondayOf = (key: string): string => {
  const d = parseKey(key);
  // getDay(): 0=日 … 6=土 → 月曜起点のオフセットに直す
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return toKey(d);
};

/** YYYY-MM (月別集計のキー) */
export const monthKeyOf = (key: string): string => key.slice(0, 7);

export const shiftMonthKey = (monthKey: string, delta: number): string => {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y ?? 1970, (m ?? 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export type Month = { year: number; month: number };

export const thisMonth = (): Month => {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

export const shiftMonth = ({ year, month }: Month, delta: number): Month => {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

export const monthRange = ({ year, month }: Month): { from: string; to: string } => ({
  from: toKey(new Date(year, month - 1, 1)),
  to: toKey(new Date(year, month, 0)),
});

/** 月表示用の 7×N グリッド。前後の月の日も埋めて返す。 */
export const monthGrid = ({ year, month }: Month): { key: string; inMonth: boolean }[] => {
  const first = new Date(year, month - 1, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay()); // 週の始まりは日曜

  const cells: { key: string; inMonth: boolean }[] = [];
  const cursor = new Date(start);
  // 6 週分あれば、どの月でも必ず収まる
  for (let i = 0; i < 42; i++) {
    cells.push({ key: toKey(cursor), inMonth: cursor.getMonth() === month - 1 });
    cursor.setDate(cursor.getDate() + 1);
  }
  // 最終週がまるごと翌月なら削る
  return cells.slice(0, cells.slice(35).every((c) => !c.inMonth) ? 35 : 42);
};
